import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./router";
import "@fontsource-variable/inter/wght.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "@/app/globals.css";
import { boundaryKind, installCrashReporter, suppressCrashReports } from "@/lib/clientCrashReport";
import { installAccountModeFetch } from "@/lib/wealth/paperClient";

// Before anything renders, so a crash during first paint is reported too.
const crash = installCrashReporter();

// Before anything fetches: while this device is in Paper mode, every request
// to an account route says so, and an answer that was not made in Paper mode
// is not shown (lib/wealth/paperClient.ts).
installAccountModeFetch();

// A deploy renames chunks by content hash. A window opened before it asks for
// names the build retired; if the server no longer has one, reload once onto
// the new index instead of failing the pane. Once a minute at most, so a
// chunk that is genuinely broken still surfaces as an error, not a loop.
window.addEventListener("vite:preloadError", (event) => {
  const KEY = "mpp:chunk-reload-at:v1";
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(KEY) || 0);
  } catch {
    /* storage blocked — still worth one reload */
  }
  if (Date.now() - last < 60_000) return;
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  event.preventDefault();
  suppressCrashReports();
  window.location.reload();
});

const el = document.getElementById("root");
if (!el) throw new Error("root element missing");

// React StrictMode stays OFF. lightweight-charts disposes under StrictMode
// in dev (Object is disposed). Pinned here; there is no next.config.ts.
// Errors an error boundary catches never reach window.onerror, so the route
// screen ("Something went wrong!") and "This pane crashed." reported nowhere.
// Overriding these replaces React's own console logging, so keep it.
createRoot(el, {
  onCaughtError(error, info) {
    const kind = boundaryKind(info.errorBoundary);
    // PaneErrorBoundary logs its own "[pane]" line.
    if (kind !== "pane") console.error(error, info.componentStack);
    crash.report(kind, error, { componentStack: info.componentStack });
  },
  onUncaughtError(error, info) {
    console.error(error, info.componentStack);
    crash.report("uncaught", error, { componentStack: info.componentStack });
  },
}).render(<RouterProvider router={router} />);
